
import React, { useEffect, useRef, useState } from 'react';
import { withApiOrigin } from "../utils/withApiOrigin";
import { useAppState } from '../store';
import { ICONS } from '../constants';
import { Category, WardrobeItem } from '../types';
import { useNavigate, Link, useLocation } from 'react-router-dom';

const STAGES = [
  "Анализируем ваш аватар...",
  "Подбираем сочетание вещей...",
  "Создаем образ...",
  "Финализируем результат..."
];

type UsageInfo = {
  plan: string;
  isAdmin: boolean;
  dailyUsed: number;
  dailyLimit: number;
  dailyRemaining: number;
  monthlyUsed: number;
  monthlyLimit: number;
  monthlyRemaining: number;
  generationCreditsRemaining: number;
};

function sourceItemToWardrobeItem(item: any): WardrobeItem {
  const id = String(item?.id || `look-item-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const image = Array.isArray(item?.images) && item.images[0]
    ? item.images[0]
    : item?.imageUrl || item?.cutoutUrl || item?.originalUrl || '';

  return {
    id,
    title: String(item?.title || 'Вещь из образа'),
    price: Number(item?.price || 0),
    currency: item?.currency || 'RUB',
    gender: item?.gender || 'UNISEX',
    category: item?.category || 'Верх',
    sizes: Array.isArray(item?.sizes) ? item.sizes : ['ONE'],
    images: image ? [image] : [],
    storeId: item?.storeId || item?.merchant || 'look-source',
    storeName: item?.storeName || item?.merchant || undefined,
    brand: item?.brand || undefined,
    productUrl: item?.productUrl || undefined,
    affiliateUrl: item?.affiliateUrl || undefined,
    availability: true,
    isCatalog: item?.isCatalog !== false,
    userId: item?.userId,
    addedAt: item?.addedAt || new Date().toISOString(),
    sourceType: item?.sourceType || 'catalog',
  } as WardrobeItem;
}

const CreateLook = () => {
  const { wardrobe, user, actions, aiError } = useAppState();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeCategory, setActiveCategory] = useState<Category | 'all'>('all');
  const [isGenerating, setIsGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [limitModal, setLimitModal] = useState<any | null>(null);
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const qualityMode: 'quality' = 'quality';
  const selfie = user?.selfieUrl || user?.avatarUrl;
  const selectedItems = wardrobe.filter((i) => selectedIds.has(i.id));
  const processedPreselectRef = useRef<string>('');

  const filteredItems = activeCategory === 'all' 
    ? wardrobe 
    : wardrobe.filter(i => i.category === activeCategory);

  const freeGenerationsAvailable = usageInfo
    ? usageInfo.dailyRemaining > 0 && usageInfo.monthlyRemaining > 0
    : true;
  const bonusGenerationsAvailable = (usageInfo?.generationCreditsRemaining || 0) > 0;
  const knownLimitBlocked = !!usageInfo && !freeGenerationsAvailable && !bonusGenerationsAvailable;

  const usageStatusText = usageInfo
    ? knownLimitBlocked
      ? 'Лимит исчерпан'
      : bonusGenerationsAvailable && !freeGenerationsAvailable
        ? 'Следующая генерация — бонусная'
        : 'Генерация доступна'
    : usageLoading
      ? 'Загружаем лимиты'
      : 'Лимиты не загружены';

  useEffect(() => {
    if (!user) {
      setUsageInfo(null);
      return;
    }

    let cancelled = false;

    const loadUsage = async () => {
      try {
        setUsageLoading(true);
        setUsageError(null);

        const res = await fetch('/api/usage/me', {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.error || 'Не удалось загрузить лимиты');
        }

        if (!cancelled) {
          setUsageInfo(data?.usage || null);
        }
      } catch (e: any) {
        if (!cancelled) {
          setUsageError(e?.message || 'Не удалось загрузить лимиты');
        }
        console.warn('[CreateLook usage] failed', e);
      } finally {
        if (!cancelled) {
          setUsageLoading(false);
        }
      }
    };

    loadUsage();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    const state = (location.state || {}) as any;
    const preselectedItems = Array.isArray(state?.preselectedItems) ? state.preselectedItems.slice(0, 5) : [];
    const preselectedItemId = state?.preselectedItemId;

    const stateKey = preselectedItems.length
      ? `items:${preselectedItems.map((i: any) => String(i?.id || i?.title || i?.imageUrl || '')).join('|')}`
      : preselectedItemId
        ? `item:${preselectedItemId}`
        : '';

    if (!stateKey) return;
    if (processedPreselectRef.current === stateKey) return;
    processedPreselectRef.current = stateKey;

    if (preselectedItems.length) {
      const normalized = preselectedItems
        .map(sourceItemToWardrobeItem)
        .filter((i: any) => i.images?.[0]);

      normalized.forEach((item) => {
        actions.upsertWardrobeItem(item);
      });

      setSelectedIds(new Set(normalized.map((i) => i.id)));

      if (normalized[0]?.category) {
        setActiveCategory(normalized[0].category as any);
      }

      return;
    }

    if (!preselectedItemId) return;
    if (!wardrobe.some((i) => i.id === preselectedItemId)) return;

    setSelectedIds((prev) => {
      if (prev.has(preselectedItemId)) return prev;
      const next = new Set(prev);
      if (next.size >= 5) return next;
      next.add(preselectedItemId);
      return next;
    });

    if (state?.preselectedCategory) {
      setActiveCategory(state.preselectedCategory);
    }
  }, [location.state, wardrobe, actions]);

  const toggleItem = (item: WardrobeItem) => {
    const next = new Set(selectedIds);
    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      if (next.size >= 5) return; // Limit to 5 items
      next.add(item.id);
    }
    setSelectedIds(next);
  };

  const handleGenerate = async () => {
    if (!selfie) {
      alert("Не удалось сгенерировать образ. Проверьте, что аватар доступен для генерации.");
      return;
    }

    if (selectedItems.length < 1) {
      alert("Выберите хотя бы одну вещь.");
      return;
    }

    if (knownLimitBlocked) {
      const monthlyBlocked = (usageInfo?.monthlyRemaining || 0) <= 0;
      setLimitModal({
        message: monthlyBlocked
          ? 'Месячный лимит генераций исчерпан.'
          : 'Лимит генераций на сегодня исчерпан. Завтра вам снова будут доступны бесплатные генерации.',
        usage: usageInfo,
      });
      return;
    }

    setIsGenerating(true);
    setGenStep(0);
    setProgress(5);

    const progressInterval = window.setInterval(() => {
      setProgress((prev) => (prev < 92 ? prev + Math.max(1, Math.round((100 - prev) / 18)) : prev));
    }, 900);

    const interval = window.setInterval(() => {
      setGenStep((prev) => (prev < STAGES.length - 1 ? prev + 1 : prev));
    }, 2200);

    try {
      const lookId = await actions.createLook(selectedItems, qualityMode);
      console.log("[CreateLook] generation finished", { lookId });

      clearInterval(interval);
      clearInterval(progressInterval);
      setGenStep(STAGES.length - 1);
      setProgress(100);

      window.setTimeout(() => {
        if (!lookId) {
          setIsGenerating(false);
          setGenStep(0);
          setProgress(0);
          alert("Сервер не вернул идентификатор образа.");
          return;
        }

        // Hard navigation is intentional here.
        // It clears any stuck overlay/state after a long generation request
        // and guarantees that the newly created look opens.
        window.location.assign(`${window.location.origin}/#/look/${lookId}`);
      }, 350);
    } catch (err: any) {
      clearInterval(interval);
      clearInterval(progressInterval);
      console.error("[CreateLook] handleGenerate error:", err);

      if (err?.message === "AUTH_REQUIRED") {
        alert("Сессия истекла. Пожалуйста, войдите снова");
        setIsGenerating(false);
        setGenStep(0);
        setProgress(0);
        window.location.hash = "#/auth";
        return;
      }

      if (err?.code === 'LOOK_GENERATION_LIMIT_REACHED') {
        if (err?.usage) {
          setUsageInfo(err.usage);
        }
        setLimitModal({
          message: err?.message || 'Лимит генераций на сегодня исчерпан',
          usage: err?.usage || null,
        });
      } else {
        alert(err?.message || aiError || "Не удалось сгенерировать образ. Проверьте соединение и настройки сервера.");
      }

      setIsGenerating(false);
      setGenStep(0);
      setProgress(0);
    }
  };

  if (!selfie) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-8 text-center space-y-8 animate-in fade-in duration-700">
        <div className="w-24 h-24 bg-zinc-50 rounded-full flex items-center justify-center border-2 border-dashed border-zinc-200">
          <ICONS.User className="w-10 h-10 text-zinc-300" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-black uppercase tracking-tight">Нужен ваш аватар</h1>
          <p className="text-sm text-zinc-400 leading-relaxed uppercase tracking-wider max-w-xs mx-auto">Для виртуальной примерки нам нужно ваше фото. Это займет всего секунду.</p>
        </div>
        <Link to="/" className="bg-zinc-900 text-white px-10 py-4 rounded-full text-xs font-black uppercase tracking-[0.2em] shadow-xl hover:scale-105 transition-transform">
          Загрузить селфи
        </Link>
      </div>
    );
  }

  return (
    <div className="pb-12">
      {/* Header Info */}
      <div className="p-6 bg-zinc-50 border-b border-zinc-100">
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 rounded-full border-2 border-white shadow-md overflow-hidden bg-zinc-200">
            <img src={withApiOrigin(selfie)} alt="Avatar" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-2xl font-black uppercase tracking-tighter">Создать образ</h1>
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.1em]">Выберите 1-5 вещей из вашего шкафа</p>
          </div>
        </div>
      </div>

      {user && (
        <div className="px-4 pt-4">
          <div className={`rounded-[28px] border p-4 space-y-3 ${
            knownLimitBlocked
              ? 'bg-rose-50 border-rose-100'
              : 'bg-white border-zinc-100 shadow-sm'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400">
                  Лимиты генераций
                </div>
                <div className={`text-sm font-black uppercase tracking-tight mt-1 ${
                  knownLimitBlocked ? 'text-rose-700' : 'text-zinc-900'
                }`}>
                  {usageStatusText}
                </div>
              </div>
              <Link
                to="/profile"
                className="shrink-0 rounded-full bg-zinc-900 px-4 py-2 text-[9px] font-black uppercase tracking-[0.18em] text-white"
              >
                Профиль
              </Link>
            </div>

            {usageInfo ? (
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-2xl bg-zinc-50 p-3">
                  <div className="text-lg font-black">{usageInfo.dailyRemaining}</div>
                  <div className="text-[8px] font-bold uppercase tracking-widest text-zinc-400">
                    сегодня из {usageInfo.dailyLimit}
                  </div>
                </div>
                <div className="rounded-2xl bg-zinc-50 p-3">
                  <div className="text-lg font-black">{usageInfo.monthlyRemaining}</div>
                  <div className="text-[8px] font-bold uppercase tracking-widest text-zinc-400">
                    месяц из {usageInfo.monthlyLimit}
                  </div>
                </div>
                <div className="rounded-2xl bg-zinc-50 p-3">
                  <div className="text-lg font-black">{usageInfo.generationCreditsRemaining}</div>
                  <div className="text-[8px] font-bold uppercase tracking-widest text-zinc-400">
                    бонусы
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-zinc-400">
                {usageLoading ? 'Загружаем ваши лимиты...' : usageError || 'Лимиты пока не загружены.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Categories Filter */}
      <div className="sticky top-[64px] z-30 bg-white border-b border-zinc-50 p-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          <button 
            onClick={() => setActiveCategory('all')}
            className={`flex-shrink-0 px-6 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-all ${activeCategory === 'all' ? 'bg-zinc-900 text-white border-zinc-900 shadow-lg' : 'bg-white border-zinc-200 text-zinc-400'}`}
          >Все</button>
          {Object.values(Category).map(cat => (
            <button 
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 px-6 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-all ${activeCategory === cat ? 'bg-zinc-900 text-white border-zinc-900 shadow-lg' : 'bg-white border-zinc-200 text-zinc-400'}`}
            >{cat}</button>
          ))}
        </div>
      </div>

      {/* Wardrobe Grid */}
      <div className="p-4 grid grid-cols-3 gap-3">
        {filteredItems.map(item => (
          <div 
            key={item.id}
            onClick={() => toggleItem(item)}
            className={`relative group aspect-square rounded-[24px] bg-zinc-50 border-2 transition-all p-2 overflow-hidden ${selectedIds.has(item.id) ? 'border-zinc-900 ring-2 ring-zinc-900 ring-inset' : 'border-zinc-100 hover:border-zinc-300'}`}
          >
            <img src={withApiOrigin(item.images?.[0])} alt="" className="w-full h-full object-contain mix-blend-multiply transition-all duration-500" />
            
            <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${selectedIds.has(item.id) ? 'bg-zinc-900 border-zinc-900 scale-110' : 'bg-white border-zinc-200'}`}>
              {selectedIds.has(item.id) && <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>}
            </div>
            
            <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-[7px] font-black uppercase bg-white/80 backdrop-blur px-1 py-0.5 rounded truncate block">{item.title}</span>
            </div>
          </div>
        ))}
        {filteredItems.length === 0 && (
          <div className="col-span-3 py-20 text-center">
            <p className="text-xs font-bold uppercase text-zinc-300 tracking-widest">В этой категории пока пусто</p>
          </div>
        )}
      </div>

      {/* Floating Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-24 left-4 right-4 z-[40] animate-in slide-in-from-bottom-8">
          <div className="bg-white rounded-[32px] p-4 shadow-2xl border border-zinc-100 flex items-center justify-between">
            <div className="flex -space-x-3 overflow-hidden ml-2">
              {wardrobe.filter(i => selectedIds.has(i.id)).map(item => (
                <div key={item.id} className="inline-block h-10 w-10 rounded-full ring-2 ring-white bg-zinc-100 overflow-hidden border border-zinc-200">
                  <img src={withApiOrigin(item.images?.[0])} className="w-full h-full object-contain mix-blend-multiply" />
                </div>
              ))}
            </div>
            <button 
              onClick={handleGenerate}
              disabled={selectedIds.size < 1 || isGenerating}
              className={`px-8 py-4 rounded-full text-[10px] font-black uppercase tracking-[0.2em] shadow-lg active:scale-95 transition-all disabled:opacity-40 ${
                knownLimitBlocked
                  ? 'bg-rose-600 text-white'
                  : 'bg-zinc-900 text-white'
              }`}
            >
              {knownLimitBlocked ? 'Лимит исчерпан' : `Стилизовать (${selectedIds.size})`}
            </button>
          </div>
        </div>
      )}

      {limitModal && (
        <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-[32px] bg-white p-6 shadow-2xl border border-zinc-100 space-y-5">
            <div className="space-y-2">
              <div className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">
                Лимит генераций
              </div>
              <h2 className="text-xl font-black uppercase tracking-tight">
                Лимит исчерпан
              </h2>
              <p className="text-sm text-zinc-500 leading-relaxed">
                {limitModal.message || 'Лимит генераций на сегодня исчерпан. Завтра вам снова будут доступны бесплатные генерации.'}
              </p>
            </div>

            <div className="rounded-2xl bg-zinc-50 border border-zinc-100 p-4 space-y-2">
              <p className="text-xs font-bold text-zinc-700">
                Хотите продолжить сейчас?
              </p>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Пригласите друга в TopTry — получите 3 дополнительные генерации после его регистрации.
              </p>
            </div>

            <div className="rounded-2xl bg-zinc-50 border border-zinc-100 p-4">
              <p className="text-xs text-zinc-500 leading-relaxed">
                Пакеты генераций скоро появятся.
              </p>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  setLimitModal(null);
                  navigate('/profile');
                }}
                className="w-full h-12 rounded-full bg-zinc-900 text-white text-[10px] font-black uppercase tracking-[0.2em]"
              >
                Пригласить друга
              </button>
              <button
                type="button"
                onClick={() => setLimitModal(null)}
                className="w-full h-12 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-black uppercase tracking-[0.2em]"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generation Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center p-10 text-center space-y-12">
          <div className="relative">
            <div className="w-40 h-40 border-[8px] border-zinc-50 border-t-zinc-900 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
               <span className="text-3xl font-black italic tracking-tighter">{Math.round(progress)}%</span>
            </div>
          </div>
          <div className="space-y-4">
            <h2 className="text-lg font-black uppercase tracking-[0.4em]">TOPTRY СОЗДАЕТ ОБРАЗ</h2>
            <div className="flex gap-2 justify-center">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className={`h-1.5 w-10 rounded-full transition-all duration-700 ${genStep >= i ? 'bg-zinc-900' : 'bg-zinc-100'}`}></div>
              ))}
            </div>
            <p className="text-[11px] text-zinc-400 font-bold uppercase tracking-[0.2em] mt-6 animate-pulse">
              {STAGES[genStep]}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateLook;
